import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, withStyles } from '@material-ui/core'
import { Icon } from '@iconify/react'
import styles from './styles'
import { getDrawerImages } from '../data'
import { useLocation, useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx'
import { SectionPopover } from '../../../index'
import { courseRemoveCard, getCardAction, cardCloneAction, clearCardState } from 'src/reducers/card/action'
import { isAnswerItemTypeResultCard } from 'src/model/card' // Assuming this is needed for result card check

import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import { ButtonData, ButtonType, ButtonActionType } from 'src/model/button'
import findNextCardAfterDeletion from 'src/hooks/findNextCardAfterDeletion'
import { getCardSuccess } from 'src/reducers/card/action'
import { useParams } from 'react-router-dom'
import { courseGetAction } from 'src/reducers/course/action'
import { useSnackbar } from 'notistack'
import { track } from '@amplitude/analytics-browser'
import { Tooltip } from '@material-ui/core'
import {
  CardPublishState,
  useCoursePublish,
  usePagePublishState,
} from 'src/contexts/course-publish-context'


/** The nearest ancestor that scrolls vertically. */
const scrollParentOf = element => {
  let node = element?.parentElement
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node)
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}

/** Centers `element` in its scrolling list when it isn't fully visible. */
const scrollIntoListView = element => {
  const list = scrollParentOf(element)
  if (!element || !list) return
  const box = list.getBoundingClientRect()
  const row = element.getBoundingClientRect()
  if (row.top >= box.top && row.bottom <= box.bottom) return
  list.scrollTop +=
    row.top - box.top - (list.clientHeight - row.height) / 2
}

const CardTitleTextfield = props => {
  const { classes, provided, card, setSections, sections } = props
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const { id, page, subPage } = useParams()
  const cardId = subPage
  const course = useSelector(state => state.course.course)
  const { enqueueSnackbar } = useSnackbar()
  const [hovered, setHovered] = useState(false)
  const currentCard = useSelector(state => state.card.currentCard)
  const [selected, setSelected] = useState(
    cardId?.toString() === card?.id?.toString() ||
      currentCard?.id?.toString() === card?.id?.toString(),
  )

  useEffect(() => {
    setSelected(
      cardId?.toString() === card?.id?.toString() ||
        currentCard?.id?.toString() === card?.id?.toString(),
    )
  }, [cardId, currentCard])

  // The page opened from the URL (e.g. a section picked on the Sections tab)
  // is scrolled to the middle of the table of contents when it is out of view.
  const rowRef = useRef(null)
  const isUrlPage = cardId?.toString() === card?.id?.toString()
  useEffect(() => {
    if (!isUrlPage) return undefined
    // After the list (and its sections) has laid out.
    const timer = setTimeout(() => scrollIntoListView(rowRef.current), 150)
    return () => clearTimeout(timer)
  }, [isUrlPage])
  const [drawerImage, setDrawerImage] = useState(
    getDrawerImages(card.type, selected),
  )
  const publishState = useCoursePublish()
  const pageState = usePagePublishState(card?.id)

  // While the whole course is a draft, per-page publish state is meaningless and actively
  // misleading — publishing a page would not make anyone able to see it.
  const showPublishState = Boolean(publishState?.isPublished) && pageState !== null

  const publishActions = () => {
    if (!showPublishState) {
      return []
    }

    const actions = []

    if (pageState.state === CardPublishState.DRAFT) {
      actions.push({
        text: 'Publish page',
        icon: 'mdi:tray-arrow-up',
        action: event => {
          if (event) event.stopPropagation()
          runPublishAction(
            () => publishState.publishPage(card.id),
            'Page published.',
          )
        },
      })
    }

    if (pageState.state === CardPublishState.PUBLISHED_WITH_CHANGES) {
      actions.push({
        text: 'Publish changes',
        icon: 'mdi:tray-arrow-up',
        action: event => {
          if (event) event.stopPropagation()
          runPublishAction(
            () => publishState.publishPage(card.id),
            'Changes published.',
          )
        },
      })
      actions.push({
        text: 'Discard changes',
        icon: 'mdi:undo-variant',
        action: event => {
          if (event) event.stopPropagation()
          runPublishAction(
            () => publishState.discardPageChanges(card.id),
            'Changes discarded.',
            true,
          )
        },
      })
    }

    if (pageState.state !== CardPublishState.DRAFT) {
      actions.push({
        text: 'Move to draft',
        icon: 'mdi:eye-off-outline',
        action: event => {
          if (event) event.stopPropagation()
          runPublishAction(
            () => publishState.unpublishPage(card.id),
            'Page moved to draft. Learners no longer see it.',
          )
        },
      })
    }

    return actions
  }

  const runPublishAction = async (action, successMessage, reloadCourse) => {
    handleClosePopup()

    const isOpenCard = cardId?.toString() === card?.id?.toString()

    try {
      if (reloadCourse && isOpenCard) {
        // Tear the editor down BEFORE discarding, not after. CKEditor flushes any pending autosave
        // when it is destroyed, so unmounting afterwards would write the very edits we just threw
        // away straight back to the server — discard would appear to do nothing.
        dispatch(clearCardState())
      }

      await action()

      if (reloadCourse) {
        // Discard rewrites the working copy, so the editor has to reload it.
        dispatch(courseGetAction(id))

        // CKEditor reads its content only when it is created, so updating redux alone left the
        // discarded text sitting on screen — and the next autosave wrote it back. Clearing the card
        // above unmounts the editor; refetching now mounts a fresh one on the restored content.
        if (isOpenCard) {
          dispatch(getCardAction(card.id))
        }
      }

      enqueueSnackbar(successMessage, { variant: 'success' })
    } catch (error) {
      // The editor is gone at this point, so put the page back rather than leaving a blank pane.
      if (reloadCourse && isOpenCard) {
        dispatch(getCardAction(card.id))
      }

      enqueueSnackbar('Could not update this page.', { variant: 'error' })
    }
  }
  const handleMouseEnter = () => {
    setHovered(true)
  }
  const handleMouseLeave = () => {
    setHovered(false)
  }

  useEffect(() => {
    setDrawerImage(getDrawerImages(card.type, selected))
  }, [cardId, card, window.location.pathname, currentCard, selected])

  const redirectToCard = card => {
    dispatch(
      getCardAction(null, () => {
        setTimeout(() => {
          if (isAnswerItemTypeResultCard(card.type)) {
            history.push(`/mini-course/${course.id}/page/completion`)
            return
          }
          history.push(`/mini-course/${course.id}/page/${card.id}`)
          dispatch(getCardAction(card.id))
        }, 1000)

        clearTimeout()
      }),
    )
  }
  const [openPopup, setOpenPopup] = React.useState(null)
  const handleClosePopup = () => {
    setOpenPopup(null)
  }

  const handleCopy = event => {
    if (event) event.stopPropagation()
    if (window.editor) {
      const data = window.editor.getData()
      navigator.clipboard.writeText(data)
    }
  }

  const handlePaste = event => {
    if (event) event.stopPropagation()
    if (!window.editor) return
    navigator.clipboard.readText().then(data => {
      if (!data) return
      const viewFragment = window.editor.data.processor.toView(data)
      const modelFragment = window.editor.data.toModel(viewFragment)
      window.editor.model.change(writer => {
        const root = window.editor.model.document.getRoot()
        writer.insert(modelFragment, root, 0)
      })
    })
  }

  const handleDuplicate = event => {
    if (event) event.stopPropagation()
    dispatch(
      cardCloneAction(card.id, course.id, newCard => {
        dispatch(courseGetAction(course.id))
        if (newCard && newCard.id) {
          history.push(`/mini-course/${course.id}/page/${newCard.id}`)
          dispatch(getCardAction(newCard.id))
          track('page_duplicated', { page_context: 'sidebar_kebab' })
          enqueueSnackbar('Page duplicated', { variant: 'success' })
        }
      }),
    )
  }

  const handleDeletePopup = event => {
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.ERROR,
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              e => {
                dispatch(messageBoxHideAction())
              },
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Delete',
              ButtonActionType.DELETE,
              e => {
                const nextCard = findNextCardAfterDeletion(card.id, sections)
                let newSections = sections
                newSections = newSections.map(section => {
                  section.cards = section.cards.filter(
                    sectionCard => sectionCard.id !== card.id,
                  )
                  if (section.cards.length === 0) {
                    return null
                  } else return section
                })

                newSections = newSections.filter(
                  section => section !== null && section.cards.length > 0,
                )

                // Navigate to appropriate page after deletion
                dispatch(
                  courseRemoveCard(course.id, card.id, () => {
                    setSections(newSections)
                  }),
                )

                if (nextCard) {
                  history.push(`/mini-course/${course.id}/page/${nextCard.id}`)
                  dispatch(getCardAction(nextCard.id))
                } else {
                  dispatch(getCardAction(null))
                  history.push(`/mini-course/${course.id}/page/`)
                }

                dispatch(messageBoxHideAction())
              },
            ),
          ],
          title: 'Delete Page',

          description: 'You are about to delete this page.',
          variant: MessageBoxVariant.NOTICE,
        }),
      ),
    )
  }

  const open = useSelector(state => state.course.sectionDrawerOpen)

  return (
    <Box
      ref={rowRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => {
        redirectToCard(card)
      }}
      className={clsx({
        [classes.header]: selected == false,
        [classes.headerSelected]: selected == true,
      })}
    >
      {hovered && open ? (
        <span {...provided.dragHandleProps}>
          <Icon icon="icon-park-outline:drag" />
        </span>
      ) : (
        <img src={drawerImage} alt="card-type" width="14px" height="14px" />
      )}
      {card?.title?.length == 0 || card?.title == null ? (
        <Typography
          style={{
            height: open ? '100%' : '0px',
            display: open ? 'block' : 'none',
            overflow: 'hidden',
            fontFamily: 'Inter',
            fontSize: '12px',
            fontWeight: 400,
          }}
        >
          Untitled Page
        </Typography>
      ) : (
        <Typography
          style={{
            height: open ? '100%' : '0px',
            overflow: 'hidden',

            fontFamily: 'Inter',
            fontSize: '12px',
            fontWeight: 400,
            maxWidth: '195px',
            textOverflow: 'ellipsis',
            display: open ? 'block' : 'none',
          }}
        >
          {card.title}
        </Typography>
      )}
      <div
        style={{
          display: open ? 'block' : 'none',

          flexGrow: 1,
        }}
      ></div>
      {showPublishState && pageState.state === CardPublishState.DRAFT && (
        <Tooltip title="Not visible to learners yet" placement="top">
          <span
            className={classes.draftBadge}
            style={{ display: open ? 'inline-block' : 'none' }}
          >
            Draft
          </span>
        </Tooltip>
      )}
      {showPublishState &&
        pageState.state === CardPublishState.PUBLISHED_WITH_CHANGES && (
          <Tooltip title="Unpublished changes" placement="top">
            {/* A word, not a bare dot: a lone dot in a list of thirty pages is easy to miss,
                and it reads the same as any other decoration until you hover it. */}
            <span
              className={classes.editedBadge}
              style={{ display: open ? 'inline-block' : 'none' }}
            >
              Edited
            </span>
          </Tooltip>
        )}
      <Icon
        icon={'eva:more-vertical-fill'}
        style={{
          display: open ? 'block' : 'none',
        }}
        className={classes.moreFillIcon}
        onClick={event => {
          event.stopPropagation()
          setOpenPopup(event.currentTarget)
        }}
      />
      <SectionPopover
        openPopup={openPopup}
        handleClosePopup={event => {
          if (event) {
            event.stopPropagation()
          }
          handleClosePopup()
        }}
        itemData={[
          ...publishActions(),
          {
            text: 'Duplicate',
            icon: 'system-uicons:duplicate',
            action: event => {
              if (event) event.stopPropagation()
              handleDuplicate()
            },
          },
          {
            text: 'Delete Page',
            icon: 'iconoir:trash',
            action: event => {
              if (event) event.stopPropagation()
              handleDeletePopup()
            },
          },
        ]}
      />
    </Box>
  )
}

export default withStyles(styles)(CardTitleTextfield)
